import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from './components/Layout';
import ResponsiveShadow from './components/ResponsiveShadow';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import useDefaultModal from './components/hooks/useDefaultModal';
import useReferenceData from './components/hooks/useReferenceData';
import { API_BASE_URL } from '../config/ApiConfig';
import LoadingModal from './components/modals/LoadingModal';

function formatCurrency(value) {
  if (value == null) return '';
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export default function Saldos() {
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const ano = localStorage.getItem('ano');
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { programas, contas } = useReferenceData(token, cnpj);
  const [saldos, setSaldos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortField, setSortField] = useState('idConta');
  const [sortDir, setSortDir] = useState('asc');

  // Estado para armazenar os saldos brutos
  const [rawSaldosConta, setRawSaldosConta] = useState([]);
  const [rawSaldosInvest, setRawSaldosInvest] = useState([]);

  // Carrega os saldos brutos ao montar ou mudar escola/ano
  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      axios.get(`${API_BASE_URL.SALDO_CONTA}`, {
        params: { idEscola: cnpj, ano },
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      }),
      axios.get(`${API_BASE_URL.SALDO_INVESTIMENTO}/search`, {
        params: { idEscola: cnpj, ano },
        headers: { Authorization: token ? `Bearer ${token}` : undefined }
      })
    ])
      .then(([contaResp, investimentoResp]) => {
        setRawSaldosConta(contaResp.data || []);
        setRawSaldosInvest(investimentoResp.data || []);
      })
      .catch(() => {
        setRawSaldosConta([]);
        setRawSaldosInvest([]);
        showDefaultModal('error', 'Erro ao carregar saldos.');
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line
  }, [token, cnpj, ano]);

  // Monta os saldos consolidados somente quando programas e contas estiverem carregados
  useEffect(() => {
    if (!programas || !Array.isArray(programas) || programas.length === 0) {
      setSaldos([]);
      return;
    }
    // Agrupa por conta
    const somaPorConta = {};
    for (const s of rawSaldosConta) {
      if (!somaPorConta[s.idConta]) somaPorConta[s.idConta] = 0;
      somaPorConta[s.idConta] += Number(s.saldoFinal || 0);
    }
    for (const s of rawSaldosInvest) {
      if (!somaPorConta[s.idConta]) somaPorConta[s.idConta] = 0;
      somaPorConta[s.idConta] += Number(s.saldoFinal || 0);
    }
    const resultado = Object.entries(somaPorConta).map(([idConta, total]) => {
      const contaObj = (contas || []).find(c => c.idConta === idConta || c.numConta === idConta);
      // Busca o idPrograma de um dos saldos
      let idPrograma = '';
      let programaDesc = '';
      const saldoConta = (rawSaldosConta || []).find(s => s.idConta === idConta);
      if (saldoConta) idPrograma = saldoConta.idPrograma;
      if (!idPrograma) {
        const saldoInvest = (rawSaldosInvest || []).find(s => s.idConta === idConta);
        if (saldoInvest) idPrograma = saldoInvest.idPrograma;
      }
      if (programas && idPrograma) {
        const prog = programas.find(p => p.id === idPrograma);
        programaDesc = prog ? prog.descricao : '';
      }
      return {
        idConta,
        contaLabel: contaObj ? (contaObj.nomeConta || contaObj.numConta || contaObj.idConta) : idConta,
        programaDesc,
        total
      };
    });
    setSaldos(resultado);
  }, [rawSaldosConta, rawSaldosInvest, programas, contas]);

  function getContaSortKey(item) {
    const s = item.contaLabel || item.idConta || '';
    const m = String(s).trim().match(/^(\d+)/);
    const num = m ? parseInt(m[1], 10) : NaN;
    const sufMatch = String(s).match(/-(.*)$/);
    const suffix = sufMatch ? sufMatch[1].toString().toUpperCase() : '';
    return { raw: String(s), num, suffix };
  }

  const sortedSaldos = React.useMemo(() => {
    if (!sortField) return saldos;
    const copy = [...saldos];
    copy.sort((a, b) => {
      if (sortField === 'idConta') {
        const ka = getContaSortKey(a);
        const kb = getContaSortKey(b);
        if (!isNaN(ka.num) && !isNaN(kb.num) && ka.num !== kb.num) return (ka.num - kb.num) * (sortDir === 'asc' ? 1 : -1);
        if (ka.num === kb.num) {
          const s = ka.suffix.localeCompare(kb.suffix, 'pt-BR', { sensitivity: 'base' });
          if (s !== 0) return s * (sortDir === 'asc' ? 1 : -1);
        }
        return ka.raw.localeCompare(kb.raw, 'pt-BR', { sensitivity: 'base' }) * (sortDir === 'asc' ? 1 : -1);
      }
      return 0;
    });
    return copy;
  }, [saldos, sortField, sortDir]);

  const resumoPorPrograma = React.useMemo(() => {
    const map = {};
    (Array.isArray(saldos) ? saldos : []).forEach(it => {
      const nome = String(it.programaDesc || '');
      if (!map[nome]) map[nome] = { nome, total: 0, qtd: 0 };
      map[nome].total += parseFloat(it.total) || 0;
      map[nome].qtd += 1;
    });
    return Object.values(map);
  }, [saldos]);

  return (
    <Layout>
      <LoadingModal show={isLoading} />
      <ResponsiveShadow>
        <PageHeader title="SALDOS CONSOLIDADOS POR CONTA" icon="bi-bank2" />
        <PageContent>
          {resumoPorPrograma.length > 0 && (
            <div className="mb-2 d-flex flex-wrap gap-2" style={{ maxHeight: '30vh', overflowY: 'auto', overflowX: 'hidden', flexShrink: 0 }}>
              {resumoPorPrograma.map(prog => (
                <div className="card border-0 shadow-sm" key={prog.nome || 'semprograma'} style={{ borderRadius: 10, flex: '1 1 0', minWidth: 200, maxWidth: '25%' }}>
                  <div className="card-body pt-2 pb-1 px-3">
                    <div className="d-flex justify-content-between align-items-center gap-2" style={{ backgroundColor: '#198754', color: '#fff', margin: '-0.5rem -1rem 0.5rem', padding: '0.15rem 1rem', borderRadius: '10px 10px 0 0', minHeight: '22px' }}>
                      <span className="fw-bold text-truncate" style={{ fontSize: '.62rem' }} title={prog.nome}>{prog.nome || 'Sem programa'}</span>
                      <span className="badge text-bg-light text-success flex-shrink-0" style={{ fontSize: '.62rem' }}>{prog.qtd}</span>
                    </div>
                    <div className="d-flex justify-content-between fw-bold" style={{ fontSize: '.78rem', whiteSpace: 'nowrap' }}>
                      <span>Total</span><span className="text-success">{formatCurrency(prog.total)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {isLoading ? (
            <div className="text-center py-3"><span className="spinner-border spinner-border-sm"></span></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm" style={{ fontSize: '0.95rem' }}>
                <thead className="table-dark">
                  <tr>
                    <th>#</th>
                    <th>Conta</th>
                    <th>Programa</th>
                    <th className="text-end">Saldo Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSaldos.map((item, idx) => (
                    <tr key={item.idConta}>
                      <td>{idx + 1}</td>
                      <td>{item.contaLabel}</td>
                      <td>{item.programaDesc}</td>
                      <td className="text-end">{formatCurrency(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </PageContent>
      </ResponsiveShadow>
    </Layout>
  );
}


